/**
 * Enhanced Error Boundary Component
 * 
 * Catches JavaScript errors anywhere in the child component tree,
 * logs those errors, and displays a fallback UI instead of crashing.
 * 
 * Features:
 * - Error logging with context
 * - User-friendly error messages
 * - Recovery options
 * - Development mode error details
 */

import React from 'react';
import { motion } from 'framer-motion';
import { isDevelopment, isProduction } from '../../config/env';
import { captureError } from '../../observability/logger';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null,
      errorInfo: null,
      errorId: null
    };
  }

  static getDerivedStateFromError(error) {
    // Generate unique error ID for tracking
    const errorId = `ERR-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
    return { 
      hasError: true, 
      error,
      errorId
    };
  }

  componentDidCatch(error, errorInfo) {
    // Log error details
    const errorDetails = {
      error: error?.toString(),
      stack: error?.stack,
      componentStack: errorInfo?.componentStack,
      errorId: this.state.errorId,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href,
    };

    if (isDevelopment) {
      console.error('ErrorBoundary caught an error:', error, errorInfo, errorDetails);
    }

    captureError(error, {
      source: 'react.ErrorBoundary',
      ...errorDetails,
    });

    if (isProduction) {
      // Production-only fallback log when the error reporter is unavailable.
      try {
        const existingErrors = JSON.parse(localStorage.getItem('app_errors') || '[]');
        existingErrors.push({
          ...errorDetails,
          // Limit stored errors to last 10
        });
        const recentErrors = existingErrors.slice(-10);
        localStorage.setItem('app_errors', JSON.stringify(recentErrors));
      } catch (e) {
        // Silently fail if localStorage is not available
      }
    }

    this.setState({ errorInfo });
  }

  handleReload = () => {
    // Clear error state and reload
    this.setState({ hasError: false, error: null, errorInfo: null });
    window.location.reload();
  };

  handleGoHome = () => {
    // Navigate to home and reset error state
    this.setState({ hasError: false, error: null, errorInfo: null });
    window.location.href = '/';
  };

  handleReset = () => {
    // Try to reset error boundary state
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  render() {
    if (this.state.hasError) {
      return (
        <div
          className="d-flex align-items-center justify-content-center min-vh-100 p-3 p-md-4"
          style={{
            background: "var(--color-bg)",
            minHeight: "100vh",
            color: "var(--color-text)",
          }}
        >
          <motion.div
            className="card border shadow-sm p-4 p-md-5 text-center"
            style={{
              maxWidth: "600px",
              width: "100%",
              background: "var(--color-card-bg)",
              borderColor: "var(--color-border)",
            }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
          >
            <div className="mb-4">
              <div className="text-danger mb-3 fs-1" aria-hidden="true">
                !
              </div>
              <h1 className="h4 fw-bold mb-3">Something went wrong</h1>
              <p className="text-muted mb-2 small">
                You can try again or return home. If this keeps happening, contact support with the error ID below.
              </p>
              {this.state.errorId && (
                <p className="text-muted small mb-4">
                  Error ID: <code>{this.state.errorId}</code>
                </p>
              )}
            </div>
            
            <div className="d-flex flex-column flex-sm-row flex-wrap gap-2 gap-sm-3 justify-content-center mb-4">
              <button
                type="button"
                className="btn btn-primary px-4 py-2"
                onClick={this.handleReload}
                style={{ minHeight: "44px", minWidth: "120px" }}
              >
                Reload page
              </button>
              <button
                type="button"
                className="btn btn-outline-primary px-4 py-2"
                onClick={this.handleGoHome}
                style={{ minHeight: "44px", minWidth: "120px" }}
              >
                Go home
              </button>
              <button
                type="button"
                className="btn btn-outline-secondary px-4 py-2"
                onClick={this.handleReset}
                style={{ minHeight: "44px", minWidth: "120px" }}
              >
                Try again
              </button>
            </div>
            
            {isDevelopment && this.state.error && (
              <details className="mt-4 text-start">
                <summary 
                  className="text-muted small cursor-pointer mb-2"
                  style={{ cursor: 'pointer', userSelect: 'none' }}
                >
                  Error Details (Development Only)
                </summary>
                <div
                  className="mt-2 p-3 rounded small"
                  style={{
                    maxHeight: "300px",
                    overflow: "auto",
                    background: "var(--color-bg-secondary)",
                    color: "var(--color-text)",
                  }}
                >
                  <div className="mb-2">
                    <strong>Error:</strong>
                    <pre className="mb-2 mt-1" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      {this.state.error.toString()}
                    </pre>
                  </div>
                  {this.state.error.stack && (
                    <div className="mb-2">
                      <strong>Stack Trace:</strong>
                      <pre className="mb-2 mt-1" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                        {this.state.error.stack}
                      </pre>
                    </div>
                  )}
                  {this.state.errorInfo?.componentStack && (
                    <div>
                      <strong>Component Stack:</strong>
                      <pre className="mb-0 mt-1" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                        {this.state.errorInfo.componentStack}
                      </pre>
                    </div>
                  )}
                </div>
              </details>
            )}
          </motion.div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
